import { useCallback, useEffect, useState } from "react";
import { Button, DataTable, EmptyState, Input, Pill, Select, Sheet, Textarea, useToast } from "@astrum/ui";
import type { Column, PillTone } from "@astrum/ui";
import { AUTOMATION_STATUS_LABEL, AUTOMATION_TASK_STATUSES } from "@astrum/shared";
import type { AutomationAction, AutomationTrigger } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import type { InfoPanelMember } from "./InfoPanel.js";
import "./project-automations-tab.css";

/**
 * The project's automations: a "When … then …" builder and the run log.
 * Shown to people who can manage the project; the server enforces the same.
 */

export type Automation = {
  id: string;
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  lastRunAt: string | null;
  runCount: number;
};

export type AutomationRun = {
  id: string;
  automationName: string;
  taskId: string | null;
  taskTitle: string | null;
  at: string;
  outcome: "ok" | "skipped" | "error";
  detail: string;
};

type Option = { id: string; name: string };

const PAGE = 25;
const OUTCOME_TONE: Record<AutomationRun["outcome"], PillTone> = { ok: "ok", skipped: "warn", error: "danger" };

const TRIGGER_LABEL: Record<AutomationTrigger["type"], string> = {
  task_created: "a task is created",
  status_changed: "a task's status changes",
  assignee_added: "an owner is added",
  due_approaching: "a due date is approaching",
  task_overdue: "a task becomes overdue",
};

const ACTION_LABEL: Record<AutomationAction["type"], string> = {
  notify: "Send a notification",
  set_status: "Set the status",
  move_column: "Move to a column",
  add_assignee: "Add an owner",
  set_due: "Set the due date",
  add_tag: "Add a tag",
};

/** One-line reading of a rule for the list. */
export function describeAutomation(
  rule: Pick<Automation, "trigger" | "actions">,
  lookup: { members: Option[]; columns: Option[]; tags: Option[] },
): string {
  const name = (list: Option[], id: string) => list.find((o) => o.id === id)?.name ?? "?";
  const t = rule.trigger;
  let when = `When ${TRIGGER_LABEL[t.type]}`;
  if (t.type === "status_changed" && t.to) when = `When a task moves to ${AUTOMATION_STATUS_LABEL[t.to]}`;
  if (t.type === "assignee_added" && t.userId) when = `When ${name(lookup.members, t.userId)} is added as an owner`;
  if (t.type === "due_approaching") when = `When a task is due within ${t.days} day${t.days === 1 ? "" : "s"}`;
  const then = rule.actions.map((a) => {
    switch (a.type) {
      case "notify":
        return `notify ${a.to === "assignees" ? "the owners" : a.to === "owner" ? "the project owner" : `${a.userIds?.length ?? 0} people`}`;
      case "set_status":
        return `set status to ${AUTOMATION_STATUS_LABEL[a.status]}`;
      case "move_column":
        return `move to ${name(lookup.columns, a.columnId)}`;
      case "add_assignee":
        return `add ${name(lookup.members, a.userId)} as owner`;
      case "set_due":
        return `set due in ${a.days} day${a.days === 1 ? "" : "s"}`;
      case "add_tag":
        return `tag ${name(lookup.tags, a.tagId)}`;
    }
  });
  return `${when}${t.condition ? " (filtered)" : ""}, ${then.join(", ")}.`;
}

function blankAction(type: AutomationAction["type"], lookup: { members: Option[]; columns: Option[]; tags: Option[] }): AutomationAction {
  switch (type) {
    case "notify":
      return { type, to: "assignees", message: "{task} needs attention" };
    case "set_status":
      return { type, status: "in_progress" };
    case "move_column":
      return { type, columnId: lookup.columns[0]?.id ?? "" };
    case "add_assignee":
      return { type, userId: lookup.members[0]?.id ?? "" };
    case "set_due":
      return { type, days: 3 };
    case "add_tag":
      return { type, tagId: lookup.tags[0]?.id ?? "" };
  }
}

function blankTrigger(type: AutomationTrigger["type"]): AutomationTrigger {
  return type === "due_approaching" ? { type, days: 2 } : ({ type } as AutomationTrigger);
}

/* ───────────────────────────── builder sheet ───────────────────────────── */

function RuleSheet({
  projectId,
  editing,
  members,
  columns,
  tags,
  sections,
  onSaved,
  onClose,
}: {
  projectId: string;
  editing: Automation | null;
  members: Option[];
  columns: Option[];
  tags: Option[];
  sections: Option[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const lookup = { members, columns, tags };
  const [name, setName] = useState(editing?.name ?? "");
  const [trigger, setTrigger] = useState<AutomationTrigger>(editing?.trigger ?? { type: "task_created" });
  const [actions, setActions] = useState<AutomationAction[]>(editing?.actions ?? [blankAction("notify", lookup)]);
  const [saving, setSaving] = useState(false);

  const condition = trigger.condition ?? {};
  const setCondition = (next: { tagId?: string; sectionId?: string }) => {
    const clean = Object.fromEntries(Object.entries(next).filter(([, v]) => v)) as { tagId?: string; sectionId?: string };
    setTrigger({ ...trigger, condition: Object.keys(clean).length ? clean : undefined } as AutomationTrigger);
  };
  const setAction = (index: number, next: AutomationAction) => setActions(actions.map((a, i) => (i === index ? next : a)));

  const submit = () => {
    setSaving(true);
    const body = { name, trigger, actions };
    const request = editing
      ? api.patch(`/projects/${projectId}/automations/${editing.id}`, body)
      : api.post(`/projects/${projectId}/automations`, body);
    request
      .then(() => {
        toast(editing ? "Automation saved." : "Automation added.");
        onSaved();
        onClose();
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not save the automation.", { tone: "danger" }))
      .finally(() => setSaving(false));
  };

  const optionList = (list: Option[]) => list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>);

  return (
    <Sheet open onClose={onClose} title={editing ? "Edit automation" : "New automation"}>
      <form
        className="automation-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input label="Name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoFocus required />

        <fieldset className="automation-form__group">
          <legend>When</legend>
          <Select
            label="Trigger"
            value={trigger.type}
            onChange={(e) => setTrigger(blankTrigger(e.target.value as AutomationTrigger["type"]))}
          >
            {(Object.keys(TRIGGER_LABEL) as AutomationTrigger["type"][]).map((t) => (
              <option key={t} value={t}>
                {TRIGGER_LABEL[t]}
              </option>
            ))}
          </Select>
          {trigger.type === "status_changed" && (
            <Select
              label="To status"
              value={trigger.to ?? ""}
              onChange={(e) => setTrigger({ ...trigger, to: (e.target.value || undefined) as typeof trigger.to })}
            >
              <option value="">Any status</option>
              {AUTOMATION_TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {AUTOMATION_STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          )}
          {trigger.type === "assignee_added" && (
            <Select label="Who" value={trigger.userId ?? ""} onChange={(e) => setTrigger({ ...trigger, userId: e.target.value || undefined })}>
              <option value="">Anyone</option>
              {optionList(members)}
            </Select>
          )}
          {trigger.type === "due_approaching" && (
            <Input
              label="Days before the due date"
              type="number"
              min={1}
              max={30}
              value={trigger.days}
              onChange={(e) => setTrigger({ ...trigger, days: Number(e.target.value) })}
            />
          )}
          <div className="automation-form__row">
            <Select label="Only if tagged" value={condition.tagId ?? ""} onChange={(e) => setCondition({ ...condition, tagId: e.target.value })}>
              <option value="">Any task</option>
              {optionList(tags)}
            </Select>
            <Select label="Only in section" value={condition.sectionId ?? ""} onChange={(e) => setCondition({ ...condition, sectionId: e.target.value })}>
              <option value="">Any section</option>
              {optionList(sections)}
            </Select>
          </div>
        </fieldset>

        <fieldset className="automation-form__group">
          <legend>Then</legend>
          {actions.map((action, index) => (
            <div key={index} className="automation-form__action">
              <Select
                label={`Action ${index + 1}`}
                value={action.type}
                onChange={(e) => setAction(index, blankAction(e.target.value as AutomationAction["type"], lookup))}
              >
                {(Object.keys(ACTION_LABEL) as AutomationAction["type"][]).map((t) => (
                  <option key={t} value={t}>
                    {ACTION_LABEL[t]}
                  </option>
                ))}
              </Select>
              {action.type === "notify" && (
                <>
                  <Select
                    label="Notify"
                    value={action.to}
                    onChange={(e) => setAction(index, { ...action, to: e.target.value as typeof action.to, userIds: e.target.value === "users" ? action.userIds ?? [] : undefined })}
                  >
                    <option value="assignees">The task's owners</option>
                    <option value="owner">The project owner</option>
                    <option value="users">Specific people</option>
                  </Select>
                  {action.to === "users" && (
                    <Select
                      label="Person"
                      value={action.userIds?.[0] ?? ""}
                      onChange={(e) => setAction(index, { ...action, userIds: e.target.value ? [e.target.value] : [] })}
                    >
                      <option value="">Choose…</option>
                      {optionList(members)}
                    </Select>
                  )}
                  <Textarea
                    label="Message ({task}, {project}, {status})"
                    value={action.message}
                    maxRows={4}
                    onChange={(e) => setAction(index, { ...action, message: e.target.value })}
                  />
                </>
              )}
              {action.type === "set_status" && (
                <Select label="Status" value={action.status} onChange={(e) => setAction(index, { ...action, status: e.target.value as typeof action.status })}>
                  {AUTOMATION_TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {AUTOMATION_STATUS_LABEL[s]}
                    </option>
                  ))}
                </Select>
              )}
              {action.type === "move_column" && (
                <Select label="Column" value={action.columnId} onChange={(e) => setAction(index, { ...action, columnId: e.target.value })}>
                  {optionList(columns)}
                </Select>
              )}
              {action.type === "add_assignee" && (
                <Select label="Person" value={action.userId} onChange={(e) => setAction(index, { ...action, userId: e.target.value })}>
                  {optionList(members)}
                </Select>
              )}
              {action.type === "set_due" && (
                <Input label="Due in (days from now)" type="number" min={0} max={365} value={action.days} onChange={(e) => setAction(index, { ...action, days: Number(e.target.value) })} />
              )}
              {action.type === "add_tag" && (
                <Select label="Tag" value={action.tagId} onChange={(e) => setAction(index, { ...action, tagId: e.target.value })}>
                  {optionList(tags)}
                </Select>
              )}
              {actions.length > 1 && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setActions(actions.filter((_, i) => i !== index))}>
                  Remove action
                </Button>
              )}
            </div>
          ))}
          {actions.length < 5 && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setActions([...actions, blankAction("notify", lookup)])}>
              Add action
            </Button>
          )}
        </fieldset>

        <div className="automation-form__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!name.trim()}>
            {editing ? "Save" : "Add"}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ───────────────────────────────── the tab ───────────────────────────────── */

export function ProjectAutomationsTab({
  projectId,
  members,
  canEdit,
}: {
  projectId: string;
  members: InfoPanelMember[];
  canEdit: boolean;
}) {
  const { toast } = useToast();
  const [rules, setRules] = useState<Automation[]>([]);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState<Option[]>([]);
  const [tags, setTags] = useState<Option[]>([]);
  const [sections, setSections] = useState<Option[]>([]);
  const [sheet, setSheet] = useState<{ editing: Automation | null } | null>(null);

  const memberOptions: Option[] = members.map((m) => ({ id: m.id, name: m.displayName }));

  const loadRules = useCallback(() => {
    setLoading(true);
    api
      .get<{ automations: Automation[] }>(`/projects/${projectId}/automations`)
      .then((res) => setRules(res.automations))
      .catch(() => toast("Could not load the automations.", { tone: "danger" }))
      .finally(() => setLoading(false));
  }, [projectId, toast]);

  const loadRuns = useCallback(() => {
    api
      .get<{ runs: AutomationRun[]; total: number }>(`/projects/${projectId}/automation-runs?limit=${PAGE}&offset=${offset}`)
      .then((res) => {
        setRuns(res.runs);
        setTotal(res.total);
      })
      .catch(() => toast("Could not load the run log.", { tone: "danger" }));
  }, [projectId, offset, toast]);

  useEffect(loadRules, [loadRules]);
  useEffect(loadRuns, [loadRuns]);

  // What the builder's pickers offer; best-effort, a failed one just leaves its picker empty.
  useEffect(() => {
    api.get<{ columns: { id: string; name: string }[] }>(`/projects/${projectId}/columns`).then((r) => setColumns(r.columns)).catch(() => undefined);
    api.get<{ tags: { id: string; name: string }[] }>("/tags?limit=200").then((r) => setTags(r.tags)).catch(() => undefined);
    api.get<{ sections: { id: string; name: string }[] }>(`/projects/${projectId}/sections`).then((r) => setSections(r.sections)).catch(() => undefined);
  }, [projectId]);

  const refresh = () => {
    loadRules();
    loadRuns();
  };

  const toggle = (rule: Automation) =>
    api
      .patch(`/projects/${projectId}/automations/${rule.id}`, { enabled: !rule.enabled })
      .then(refresh)
      .catch(() => toast("Could not update the automation.", { tone: "danger" }));

  const remove = (rule: Automation) => {
    if (!window.confirm(`Delete "${rule.name}"?`)) return;
    api
      .delete(`/projects/${projectId}/automations/${rule.id}`)
      .then(refresh)
      .catch(() => toast("Could not delete the automation.", { tone: "danger" }));
  };

  const lookup = { members: memberOptions, columns, tags };

  const ruleColumns: Column<Automation>[] = [
    { key: "name", header: "Name", rowHeader: true, render: (r) => r.name },
    { key: "rule", header: "Rule", render: (r) => describeAutomation(r, lookup) },
    { key: "state", header: "State", render: (r) => <Pill tone={r.enabled ? "ok" : "neutral"}>{r.enabled ? "On" : "Off"}</Pill> },
    { key: "runs", header: "Runs", render: (r) => r.runCount },
    ...(canEdit
      ? [
          {
            key: "actions",
            header: "",
            align: "end" as const,
            render: (r: Automation) => (
              <span className="automation-tab__row-actions">
                <Button size="sm" variant="ghost" onClick={() => void toggle(r)}>
                  {r.enabled ? "Turn off" : "Turn on"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSheet({ editing: r })}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r)}>
                  Delete
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  const runColumns: Column<AutomationRun>[] = [
    { key: "at", header: "When", rowHeader: true, render: (r) => new Date(r.at).toLocaleString() },
    { key: "rule", header: "Automation", render: (r) => r.automationName },
    { key: "task", header: "Task", render: (r) => r.taskTitle ?? "—" },
    { key: "outcome", header: "Outcome", render: (r) => <Pill tone={OUTCOME_TONE[r.outcome]}>{r.outcome}</Pill> },
    { key: "detail", header: "Detail", render: (r) => r.detail },
  ];

  return (
    <div className="automation-tab">
      <div className="automation-tab__bar">
        <h2 className="automation-tab__title">Automations</h2>
        {canEdit && <Button onClick={() => setSheet({ editing: null })}>Add automation</Button>}
      </div>
      <DataTable
        caption="Project automations"
        columns={ruleColumns}
        rows={rules}
        rowKey={(r) => r.id}
        loading={loading}
        empty={<EmptyState title="No automations yet" description={canEdit ? "Add a rule to notify, move or assign work automatically." : undefined} />}
      />

      <h2 className="automation-tab__title">Run log</h2>
      <DataTable
        caption="Automation runs"
        columns={runColumns}
        rows={runs}
        rowKey={(r) => r.id}
        empty={<EmptyState title="Nothing has run yet" />}
      />
      {total > PAGE && (
        <div className="automation-tab__pager">
          <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <Button size="sm" variant="secondary" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            Next
          </Button>
        </div>
      )}

      {sheet && (
        <RuleSheet
          projectId={projectId}
          editing={sheet.editing}
          members={memberOptions}
          columns={columns}
          tags={tags}
          sections={sections}
          onSaved={refresh}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}
